import { keepPreviousData, skipToken, useQuery } from '@tanstack/react-query';
import { getV1CompaniesCompanyIdTaxRateSchedulesId } from '@treadinc/horizon-api-spec';

import { TaxRateSchedule } from '~hooks/useTaxRateSchedule/models';
import {
  RARELY_CHANGED_DATA_STALE_TIME_IN_MS,
  TAX_RATE_SCHEDULE_QUERY_KEY,
} from '~queries/constants';
import { Nullable } from '~types/Nullable';

interface UseTaxRateScheduleQueryProps {
  companyId?: string;
  taxRateScheduleId?: Nullable<string>;
}

// A failed lookup is reported through isLookupError; callers decide whether to surface it.
export default function useTaxRateScheduleQuery({
  companyId,
  taxRateScheduleId,
}: UseTaxRateScheduleQueryProps) {
  const { data, isError } = useQuery({
    queryKey: [TAX_RATE_SCHEDULE_QUERY_KEY, companyId, taxRateScheduleId],
    queryFn:
      companyId && taxRateScheduleId
        ? async ({ signal }) => {
            const response = await getV1CompaniesCompanyIdTaxRateSchedulesId({
              path: { 'company-id': companyId, id: taxRateScheduleId },
              signal,
            });

            return TaxRateSchedule.parse(response.data.data);
          }
        : skipToken,
    // keeps the previous label on screen while a newly picked schedule loads, so a save
    // never blanks the field
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: RARELY_CHANGED_DATA_STALE_TIME_IN_MS,
  });

  return {
    isLookupError: Boolean(taxRateScheduleId) && isError,
    taxRateSchedule: taxRateScheduleId ? (data ?? null) : null,
  };
}
